import React from 'react';

interface Source {
  id: number;
  name: string;
}

interface ThreatCategory {
  id: number;
  code: string;
  name: string;
  isActive: boolean;
  deletedAt?: string | null;
}

interface FilterState {
  search: string;
  analysisStatus: 'all' | 'analyzed' | 'pending' | 'unanalyzed' | 'not_relevant';
  minRiskScore: number;
  threatCategory: string;
  sortBy: 'date' | 'risk' | 'title';
  sortOrder: 'asc' | 'desc';
}

interface ArticlesFiltersProps {
  filters: FilterState;
  setFilters: (filters: FilterState) => void;
  sources: Source[];
  categories: ThreatCategory[];
  selectedSourceId: number | null;
  setSelectedSourceId: (id: number | null) => void;
}

export function ArticlesFilters({
  filters,
  setFilters,
  sources,
  categories,
  selectedSourceId,
  setSelectedSourceId
}: ArticlesFiltersProps) {
  const handleClearFilters = () => {
    setFilters({
      search: '',
      analysisStatus: 'all',
      minRiskScore: 0,
      threatCategory: '',
      sortBy: 'date',
      sortOrder: 'desc'
    });
    setSelectedSourceId(null);
  };

  return (
    <div className="mb-6 space-y-4 border rounded-lg p-4 bg-gray-50">
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        {/* Search */}
        <div>
          <label className="block text-sm font-medium tc-text-secondary mb-1">Search</label>
          <input
            type="text"
            value={filters.search}
            onChange={(e) => setFilters({ ...filters, search: e.target.value })}
            placeholder="Search title, content, URL..."
            className="tc-input w-full"
          />
        </div>
        
        {/* Source Filter */}
        <div>
          <label className="block text-sm font-medium tc-text-secondary mb-1">Source</label>
          <select
            value={selectedSourceId || ''}
            onChange={(e) => {
              const newSourceId = e.target.value ? parseInt(e.target.value) : null;
              setSelectedSourceId(newSourceId);
            }}
            className="tc-input w-full"
          >
            <option value="">All Sources</option>
            {sources.map(source => (
              <option key={source.id} value={source.id}>
                {source.name}
              </option>
            ))}
          </select>
        </div>
        
        {/* Analysis Status */}
        <div>
          <label className="block text-sm font-medium tc-text-secondary mb-1">Analysis Status</label>
          <select
            value={filters.analysisStatus}
            onChange={(e) => setFilters({ ...filters, analysisStatus: e.target.value as FilterState['analysisStatus'] })}
            className="tc-input w-full"
          >
            <option value="all">All Articles</option>
            <option value="analyzed">✅ Analyzed</option>
            <option value="pending">🔄 Processing</option>
            <option value="unanalyzed">⏳ Unanalyzed</option>
            <option value="not_relevant">❌ Not Relevant</option>
          </select>
        </div>
        
        {/* Risk Score Filter */}
        <div>
          <label className="block text-sm font-medium tc-text-secondary mb-1">Min Risk Score</label>
          <select
            value={filters.minRiskScore}
            onChange={(e) => setFilters({ ...filters, minRiskScore: parseInt(e.target.value) })}
            className="tc-input w-full"
          >
            <option value="0">Any Risk</option>
            <option value="3">≥ 3 (Low)</option>
            <option value="5">≥ 5 (Medium)</option>
            <option value="7">≥ 7 (High)</option>
            <option value="9">≥ 9 (Critical)</option>
          </select>
        </div>
      </div>
      
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        {/* Threat Category Filter */}
        <div>
          <label className="block text-sm font-medium tc-text-secondary mb-1">Threat Category</label>
          <select
            value={filters.threatCategory}
            onChange={(e) => setFilters({ ...filters, threatCategory: e.target.value })}
            className="tc-input w-full"
          >
            <option value="">All Categories</option>
            {categories.map(cat => (
              <option key={cat.code} value={cat.code}>
                {cat.name} ({cat.code})
              </option>
            ))}
          </select>
        </div>
        
        {/* Sort By */}
        <div>
          <label className="block text-sm font-medium tc-text-secondary mb-1">Sort By</label>
          <select
            value={filters.sortBy}
            onChange={(e) => setFilters({ ...filters, sortBy: e.target.value as FilterState['sortBy'] })}
            className="tc-input w-full"
          >
            <option value="date">Date</option>
            <option value="risk">Risk Score</option>
            <option value="title">Title</option>
          </select>
        </div>
        
        {/* Sort Order */}
        <div>
          <label className="block text-sm font-medium tc-text-secondary mb-1">Order</label>
          <select
            value={filters.sortOrder}
            onChange={(e) => setFilters({ ...filters, sortOrder: e.target.value as FilterState['sortOrder'] })}
            className="tc-input w-full"
          >
            <option value="desc">Descending ↓</option>
            <option value="asc">Ascending ↑</option>
          </select>
        </div>
        
        {/* Clear Filters */}
        <div className="flex items-end">
          <button
            onClick={handleClearFilters}
            className="tc-btn tc-btn-secondary w-full"
          >
            🔄 Clear Filters
          </button>
        </div>
      </div>
    </div>
  );
}